import {
  AccessTimeRounded as AccessTimeRoundedIcon,
  CheckCircle as CheckCircleIcon,
  LockOpen as LockOpenIcon,
  NotInterested as NotInterestedIcon,
} from "@mui/icons-material";
import { Chip } from "@mui/material";
import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { scopeDisplayMap } from "../../utils/requestBodies.js";
import DataTable from "./DataTable";
import { useExistingTableData } from "./useExistingTableData";

const ChangeEventTable = ({ projectId, companyId, refreshTrigger, title }) => {
  const [data] = useExistingTableData(
    `${process.env.REACT_APP_API_BASE_URL}/change-management/companies/${companyId}/projects/${projectId}/change_events/`,
    projectId,
    companyId,
    refreshTrigger,
    "Failed to fetch change events"
  );

  const flattened = useMemo(() => {
    return (data || []).map((changeEvent) => ({
      id: changeEvent.id,
      number: changeEvent.number || "N/A",
      title: changeEvent.title || "N/A",
      description: changeEvent.description || "N/A",
      status_name: changeEvent.status?.name || "N/A",
      status_id: changeEvent.status?.id || "N/A",
      origin_name:
        changeEvent.event_origin?.display_name || changeEvent.event_origin?.name || "N/A",
      origin_id: changeEvent.event_origin?.origin_id || changeEvent.event_origin?.id || "N/A",
      change_type: changeEvent.change_type?.name || "No Type Set",
      change_reason: changeEvent.change_reason?.change_reason || "N/A",
      scope: scopeDisplayMap[changeEvent.scope] || changeEvent.scope || "N/A",
      created_at: changeEvent.created_at
        ? new Date(changeEvent.created_at).toLocaleDateString()
        : "N/A",
    }));
  }, [data]);

  const getStatusChip = (status = "N/A") => {
    const statusValue = status;

    if (statusValue === "Closed") {
      return (
        <Chip
          label={statusValue}
          size="small"
          sx={{
            backgroundColor: "#e8f5e9",
            color: "#2e7d32",
            border: "1px solid #2e7d32",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            lineHeight: 1.5,
            "& .MuiChip-icon": {
              color: "#2e7d32",
            },
          }}
          icon={<CheckCircleIcon fontSize="small" />}
        />
      );
    } else if (statusValue === "Open") {
      return (
        <Chip
          label={statusValue}
          size="small"
          sx={{
            backgroundColor: "#e3f2fd",
            color: "#1976d2",
            border: "1px solid #1976d2",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            lineHeight: 1.5,
            "& .MuiChip-icon": {
              color: "#1976d2",
            },
          }}
          icon={<LockOpenIcon fontSize="small" />}
        />
      );
    } else if (statusValue === "Void") {
      return (
        <Chip
          label={statusValue}
          size="small"
          sx={{
            backgroundColor: "#ffebee",
            color: "#c62828",
            border: "1px solid #c62828",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            lineHeight: 1.5,
            "& .MuiChip-icon": {
              color: "#c62828",
            },
          }}
          icon={<NotInterestedIcon fontSize="small" />}
        />
      );
    } else if (statusValue === "Pending") {
      return (
        <Chip
          label={statusValue}
          size="small"
          sx={{
            backgroundColor: "#fff3cd",
            color: "#d84315",
            border: "1px solid #d84315",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            lineHeight: 1.5,
            "& .MuiChip-icon": {
              color: "#d84315",
            },
          }}
          icon={<AccessTimeRoundedIcon fontSize="small" />}
        />
      );
    } else {
      return (
        <Chip
          label={statusValue}
          size="small"
          color="default"
          sx={{
            display: "flex",
            alignItems: "center",
            lineHeight: 1.5,
          }}
        />
      );
    }
  };

  const columns = [
    { field: "number", headerName: "Number" },
    { field: "title", headerName: "Title" },
    { field: "description", headerName: "Description" },
    { field: "change_type", headerName: "Type" },
    { field: "scope", headerName: "Scope" },
    {
      field: "status_name",
      headerName: "Status",
      Cell: (value) => getStatusChip(value),
    },
    { field: "origin_name", headerName: "Origin" },
    { field: "change_reason", headerName: "Change Reason" },
    { field: "created_at", headerName: "Created At" },
  ];

  return (
    <DataTable
      data={flattened}
      columns={columns}
      noDataMessage="No Existing Change Event Requests Found"
      title={title}
    />
  );
};

ChangeEventTable.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  refreshTrigger: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]).isRequired,
  title: PropTypes.string,
};

export default ChangeEventTable;
